"use client";

import React, { useState } from "react";

interface FaqItem {
  faq_question?: string;
  question?: string;
  title?: string;
  faq_answer?: string;
  answer?: string;
}

interface ProductFaqsProps {
  faqs: FaqItem[];
}

const ProductFaqs: React.FC<ProductFaqsProps> = ({ faqs }) => {
  const [showAll, setShowAll] = useState(false);

  if (!faqs || faqs.length === 0) return null;

  const displayedFaqs = showAll ? faqs : faqs.slice(0, 4);
  const hasMore = faqs.length > 4;

  return (
    <div className="bg-white rounded-2xl border border-neutral-100 p-6 md:p-8 shadow-[0_12px_35px_rgba(0,0,0,0.015)] relative overflow-hidden">
      {/* Accent Line */}
      <div className="absolute top-0 left-0 w-full h-[4px] bg-gradient-to-r from-[#FF5A00] to-transparent" />

      {/* Header */}
      <h3 className="text-[10px] font-black uppercase tracking-[0.15em] text-neutral-600 mb-6 flex items-center gap-2.5">
        <span className="w-1.5 h-1.5 bg-[#FF5A00] rotate-45 block shrink-0 shadow-sm shadow-orange-500/20" />
        <span>Frequently Asked Questions</span>
      </h3>

      {/* FAQ Items */}
      <div className="space-y-3">
        {displayedFaqs.map((faq, index) => (
          <details
            key={index}
            className="group border border-neutral-100 rounded-xl bg-neutral-50/50 overflow-hidden transition-all duration-200 [&[open]]:bg-white [&[open]]:border-neutral-200 [&[open]]:shadow-sm"
          >
            <summary className="flex items-center justify-between p-4 cursor-pointer list-none select-none text-xs md:text-sm font-bold text-neutral-900 group-open:text-[#FF5A00] transition-colors">
              <span>{faq?.faq_question || faq?.question || faq?.title}</span>
              <span className="ml-4 shrink-0 w-6 h-6 rounded-full bg-neutral-100 group-open:bg-orange-50 text-neutral-400 group-open:text-[#FF5A00] flex items-center justify-center transition-transform duration-300 group-open:rotate-180">
                <svg className="w-3 h-3 fill-current" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </summary>
            <div className="px-4 pb-4 pt-1 text-xs md:text-sm text-neutral-600 leading-relaxed border-t border-neutral-100/60 font-normal">
              {faq?.faq_answer || faq?.answer ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: faq?.faq_answer || faq?.answer || "",
                  }}
                />
              ) : null}
            </div>
          </details>
        ))}
      </div>

      {/* Show More / Show Less Button */}
      {hasMore && (
        <div className="mt-6 pt-4 flex justify-center">
          <button
            onClick={() => setShowAll((prev) => !prev)}
            className="inline-flex items-center justify-center gap-2 py-2.5 px-6 rounded-xl border border-orange-200 bg-orange-50/50 text-[#FF5A00] hover:bg-[#FF5A00] hover:text-white font-bold text-xs uppercase tracking-wider transition-all duration-300 shadow-sm"
          >
            <span>
              {showAll ? "Show Less" : `Show More`}
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductFaqs;